import { SELECTABLE_CLASSES } from "@tank/shared";

export interface LobbyResult {
  name: string;
  mode: "deathmatch" | "coop" | "koth";
  action: "create" | "join";
  code?: string;
  classId: string;
}

/** Pre-game overlay: pick name + class + mode, then create a room or join by code. */
export class LobbyUI {
  readonly el: HTMLElement;
  private status: HTMLElement;

  constructor(private onSubmit: (r: LobbyResult) => void) {
    const classOptions = SELECTABLE_CLASSES
      .map((c) => `<option value="${c.id}">${c.name} — ${c.role} · ⚡${c.ability.name}</option>`)
      .join("");

    this.el = document.createElement("div");
    Object.assign(this.el.style, {
      position: "fixed", inset: "0", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", gap: "12px",
      background: "rgba(8,12,16,0.96)", color: "#e9eef2",
      fontFamily: "system-ui, sans-serif", zIndex: "40",
    });
    this.el.innerHTML = `
      <div style="font-size:34px;font-weight:700;letter-spacing:1px">TANK <span style="color:#2a9d8f">ARENA</span></div>
      <div style="opacity:.65;font-size:13px;margin-top:-8px">WASD move · mouse aim · click fire · Shift special</div>
      <input id="li-name" placeholder="Nickname" maxlength="16"
        style="width:260px;padding:10px 12px;font-size:15px;background:#141a21;border:1px solid #2a3a4a;border-radius:6px;color:#fff" />
      <div style="display:flex;gap:14px;align-items:center;font-size:13px">
        <label style="display:flex;flex-direction:column;gap:3px;opacity:.85">Class
          <select id="li-class" style="padding:8px;background:#141a21;border:1px solid #2a3a4a;border-radius:6px;color:#fff">${classOptions}</select>
        </label>
        <label style="display:flex;flex-direction:column;gap:3px;opacity:.85">Mode
          <select id="li-mode" style="padding:8px;background:#141a21;border:1px solid #2a3a4a;border-radius:6px;color:#fff">
            <option value="deathmatch">Deathmatch</option>
            <option value="coop">Coop vs Bots</option>
            <option value="koth">King of the Hill</option>
          </select>
        </label>
      </div>
      <div id="li-perk" style="width:300px;min-height:34px;font-size:12px;line-height:1.4;text-align:center;color:#8fb9c2"></div>
      <button id="li-create" style="width:280px;padding:11px;font-size:15px;font-weight:600;background:#2a9d8f;border:0;border-radius:6px;color:#06120f;cursor:pointer">Create Room</button>
      <div style="display:flex;gap:6px;align-items:center">
        <input id="li-code" placeholder="Room code" style="width:180px;padding:9px;background:#141a21;border:1px solid #2a3a4a;border-radius:6px;color:#fff" />
        <button id="li-join" style="padding:9px 14px;background:#264653;border:0;border-radius:6px;color:#fff;cursor:pointer">Join</button>
      </div>
      <div id="li-status" style="height:18px;font-size:13px;color:#e76f51"></div>
      <div style="position:fixed;bottom:10px;font-size:11px;opacity:.45;text-align:center">
        Dinosaure 3D par <a href="https://sketchfab.com/zixisun51" target="_blank" rel="noopener noreferrer" style="color:inherit">zixisun02</a> (Sketchfab)
      </div>
    `;
    document.body.appendChild(this.el);
    this.status = this.el.querySelector("#li-status")!;
    const perkEl = this.el.querySelector("#li-perk") as HTMLElement;

    const val = (id: string) => (this.el.querySelector(id) as HTMLInputElement | HTMLSelectElement).value;
    const name = () => val("#li-name").trim() || "Player";
    const mode = () => val("#li-mode") as "deathmatch" | "coop" | "koth";
    const classId = () => val("#li-class");

    // Live perk blurb for the selected class (special name + description).
    const perkByClass = new Map(SELECTABLE_CLASSES.map((c) => [c.id, c.ability]));
    const refreshPerk = () => {
      const a = perkByClass.get(classId());
      perkEl.innerHTML = a
        ? `<b style="color:#2a9d8f">⚡ ${a.name}</b> — ${a.desc}`
        : "";
    };
    (this.el.querySelector("#li-class") as HTMLSelectElement).addEventListener("change", refreshPerk);
    refreshPerk();

    (this.el.querySelector("#li-create") as HTMLButtonElement).addEventListener("click", () => {
      this.onSubmit({ name: name(), mode: mode(), action: "create", classId: classId() });
    });
    (this.el.querySelector("#li-join") as HTMLButtonElement).addEventListener("click", () => {
      const c = val("#li-code").trim();
      if (!c) { this.setStatus("Enter a room code to join."); return; }
      this.onSubmit({ name: name(), mode: mode(), action: "join", code: c, classId: classId() });
    });
  }

  setStatus(msg: string): void {
    this.status.textContent = msg;
  }

  hide(): void {
    this.el.style.display = "none";
  }

  show(): void {
    this.el.style.display = "flex";
  }
}
